import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  ListItemButton,
} from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';

const BRAND_BLUE = '#0d4efe';
const BRAND_GREEN = '#01ebb2';
const TEXT_DEFAULT = '#0f172a';
const ICON_DEFAULT = '#475569';
const DIVIDER_TINT = 'rgba(13, 78, 254, 0.18)';

const ADMIN_NAVIGATION = [
  { kind: 'header', title: 'Admin' },
  {
    id: 'metrics',
    title: 'Metrics',
    icon: <ShowChartOutlinedIcon />,
  },
  {
    id: 'crm',
    title: 'Outreach CRM',
    icon: <PeopleAltOutlinedIcon />,
  },
  {
    id: 'waitlist',
    title: 'Lead Match Waitlist',
    icon: <InboxOutlinedIcon />,
  },
  {
    id: 'users',
    title: 'User Management',
    icon: <ManageAccountsOutlinedIcon />,
  },
  {
    id: 'businesses',
    title: 'Business Management',
    icon: <StorefrontOutlinedIcon />,
  },
  {
    id: 'reviews',
    title: 'Pending Reviews',
    icon: <RateReviewOutlinedIcon />,
  },
];

export function getAdminTabTitle(tabId) {
  const item = ADMIN_NAVIGATION.find((entry) => entry.id === tabId);
  return item?.title ?? 'Admin';
}

function AdminNavigationBar({ activeTab, onTabChange }) {
  const renderNavigationItem = (item, index) => {
    if (item.kind === 'header') {
      return (
        <ListItem key={index} sx={{ py: 1, px: 2 }}>
          <Typography
            variant="overline"
            sx={{
              fontWeight: 800,
              letterSpacing: 0.6,
              color: BRAND_BLUE,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: BRAND_GREEN,
              }}
            />
            {item.title}
          </Typography>
        </ListItem>
      );
    }

    const active = activeTab === item.id;

    return (
      <ListItem key={item.id} disablePadding sx={{ mb: 0.25 }}>
        <ListItemButton
          onClick={() => onTabChange(item.id)}
          aria-current={active ? 'page' : undefined}
          selected={active}
          sx={{
            bgcolor: '#fff',
            '&.Mui-selected, &.Mui-selected:hover': { bgcolor: '#fff' },
            borderRadius: 1.5,
            pl: 1.5,
            pr: 1.5,
            py: 1.1,
            alignItems: 'flex-start',
            position: 'relative',
            color: active ? BRAND_BLUE : TEXT_DEFAULT,
            transition: 'color .15s ease, box-shadow .15s ease',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 8,
              bottom: 8,
              width: active ? 4 : 0,
              backgroundColor: BRAND_GREEN,
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
              transition: 'width .15s ease',
            },
            '&:hover::before': { width: 4 },
            '&:focus-visible': {
              outline: `2px solid ${BRAND_BLUE}`,
              outlineOffset: 2,
            },
            '&:hover': {
              bgcolor: '#fff',
              boxShadow: '0 1px 0 rgba(2,6,23,0.06)',
            },
            '& .MuiListItemIcon-root': {
              color: active ? BRAND_BLUE : ICON_DEFAULT,
              minWidth: 32,
              mt: 0.15,
              transition: 'color .15s ease',
            },
            '& .MuiListItemText-primary': {
              fontWeight: active ? 700 : 500,
              whiteSpace: 'normal',
              lineHeight: 1.35,
              fontSize: '0.875rem',
            },
          }}
        >
          {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
          <ListItemText primary={item.title} />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <Box sx={{ width: '100%', bgcolor: '#fff' }}>
      <List>{ADMIN_NAVIGATION.map(renderNavigationItem)}</List>
      <Divider sx={{ mt: 1.5, borderColor: DIVIDER_TINT }} />
    </Box>
  );
}

AdminNavigationBar.propTypes = {
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
};

export default AdminNavigationBar;
